import React, { Fragment, useState, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Link, withRouter } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Container, Logo, PreHeader } from '@scentregroup/ui'
import { getCentreStructuredData } from '../../helpers/get-centre-structured-data'
import getCentreOpenGraphData from '../../helpers/get-centre-open-graph-data'
import CentreFooter from '../../components/centre-footer'
import { UniversalFooter } from '../../components/universal-footer'
import FeedbackButton from '../../components/feedback-button'
import Header from '../../components/header'
import UniversalHeader, { useCart } from '../../components/universal-header'
import useUser from '../../components/universal-header/use-user'
import withCentreSelector from '../../containers/with-centre-selector'
import withGiftCards from '../../containers/with-gift-cards'
import withCentre from '../../containers/with-centre'
import withNationalNavLinks from '../../containers/with-national-nav-links'
import containerStyle from '../../components/container.scss'
import { centreType } from '../../types'
import pagesHoc from '../pages-hoc'
import CentrePattern from '../../components/centre-pattern'
import {
  TimeZoneContext,
  CustomThemeContext,
  BasicPageContext,
  AgentInfoContext,
  useCountryContext,
} from '../../context'
import {
  CONFIG_KEYS,
  getAlgoliaApiKeys,
  getClientConfigValue,
} from '../../helpers/config-helper'
import { TouchEventDetection } from '../../helpers/with-touch-detection'
import fullCentreTitle from '../../helpers/full-centre-title'
import { useRecentCentresTracker } from '../../hooks/use-recent-centres'
import style from './index.scss'

const identityClientUrl = getClientConfigValue(
  CONFIG_KEYS.NEXT_PUBLIC_IDENTITY_CLIENT_URL
)

const isMapPage = location => location.pathname.includes('centre-map')
const shouldAnimateW = true

const CentrePage = ({
  centres = [],
  giftCards,
  nationalNavLinks,
  centre,
  isEvolved = false,
  children,
  location,
}) => {
  const country = useCountryContext()
  const cart = useCart(isEvolved ? useQuery : undefined)
  const { user } = useUser(isEvolved ? useQuery : undefined)

  useRecentCentresTracker(centre)
  const [firstPage, setFirstPage] = useState(shouldAnimateW)
  useEffect(() => {
    if (firstPage) {
      setFirstPage(false)
    }
  }, [firstPage, location.pathname])
  const preHeaderColour =
    centre.bespoke && centre.bespoke.luxury ? 'minimal' : 'seasonal'
  return (
    <BasicPageContext.Consumer>
      {isBasicPage => (
        <TimeZoneContext.Provider value={centre.timeZone}>
          <CustomThemeContext.Provider value={centre.bespoke}>
            <TouchEventDetection className="fullHeightForMap">
              {!isBasicPage &&
                (isEvolved ? (
                  <UniversalHeader
                    key={centre.slug}
                    Link={Link}
                    country={country}
                    centres={centres}
                    giftCards={giftCards}
                    centre={centre}
                    title={{
                      label: centre.title,
                      url: `/${centre.slug}`,
                    }}
                    cart={{
                      url: '/shop/cart2',
                      count: cart?.marketplaceItems?.length,
                    }}
                    user={user}
                    pinnedLinks={{
                      items: [
                        {
                          label: 'Opening hours',
                          url: `/${centre.slug}/opening-hours`,
                        },
                        {
                          label: 'Browse stores',
                          url: `/${centre.slug}/browse-stores`,
                        },
                        {
                          label: "What's happening",
                          url: `/${centre.slug}/whats-happening`,
                        },
                        { label: 'Gift Cards', url: `/gift-cards` },
                      ],
                    }}
                    account={{
                      baseUrl: identityClientUrl,
                      from: isEvolved ? '' : 'westfield',
                    }}
                    algoliaApiKeys={getAlgoliaApiKeys()}
                    nationalNav={nationalNavLinks}
                  />
                ) : (
                  <Fragment>
                    <CentrePattern slug={centre.slug} />
                    <AgentInfoContext.Consumer>
                      {({ isMobile }) =>
                        (!isMapPage(location) || !isMobile) && (
                          <PreHeader colour={preHeaderColour}>
                            <Link
                              to="/"
                              title="Home"
                              className={style.headerLogo}
                            >
                              <Logo />
                            </Link>
                          </PreHeader>
                        )
                      }
                    </AgentInfoContext.Consumer>
                    <Header
                      isCentreHome
                      centre={centre}
                      navigationMenu={centre.navigationMenu}
                    />
                  </Fragment>
                ))}
              <Container
                className={classNames(
                  containerStyle.relativelyPositioned,
                  containerStyle.containerSpaced,
                  containerStyle.allowForHeader,
                  'fullHeightForMap'
                )}
              >
                {children({ centre, firstPage })}
              </Container>

              {!isBasicPage && (
                <Fragment>
                  <div id="search-modal" />
                  {!isMapPage(location) && (
                    <Fragment>
                      {isEvolved ? (
                        <UniversalFooter centre={centre} />
                      ) : (
                        <CentreFooter centre={centre} />
                      )}
                      <FeedbackButton emailAddress={centre.emailAddress} />
                    </Fragment>
                  )}
                </Fragment>
              )}
            </TouchEventDetection>
          </CustomThemeContext.Provider>
        </TimeZoneContext.Provider>
      )}
    </BasicPageContext.Consumer>
  )
}

CentrePage.propTypes = {
  centres: PropTypes.arrayOf(PropTypes.object),
  giftCards: PropTypes.object,
  centre: centreType,
  location: PropTypes.object,
  children: PropTypes.func.isRequired,
  isEvolved: PropTypes.bool,
  nationalNavLinks: PropTypes.object,
}

const getMetaData = ({ canonical, centre }) => {
  const title = fullCentreTitle(centre)
  const description = `Westfield ${centre.title} - your one-stop hub for shopping, fun & relaxation. Shop our range of fashion, entertainment, dining & wellbeing or check out great events & activities.`
  const metaAttributes = {
    url: canonical,
    centre,
    name: title,
    description,
  }
  return {
    title,
    description,
    openGraphData: getCentreOpenGraphData(metaAttributes),
    schema: getCentreStructuredData(metaAttributes),
  }
}

export const RawCentrePage = CentrePage

export const CentreShell = withRouter(
  withCentreSelector(
    withGiftCards(
      withCentre(withNationalNavLinks(pagesHoc(RawCentrePage, getMetaData)))
    )
  )
)
